import { useQueries, useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  CampaignApiRecord,
  CampaignApiStatus,
  CampaignsListQuery,
  CampaignsListResponse,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { retryOnTransientErrors } from "./queryUtils";
import { unwrapGamePassCollection } from "./responseMappers";

/**
 * Fetcher for `GET /gamepass/v2/admin/campaigns`. Pure (no React) so callers
 * outside a component tree can drain it directly. The `status` filter is only
 * forwarded when provided so the unfiltered listing URL stays clean.
 */
export const fetchCampaignsList = async (
  params: CampaignsListQuery = {},
): Promise<CampaignsListResponse> => {
  const response = await getPrivateInstance().get<CampaignsListResponse>(
    API_ROUTES.GET_CAMPAIGNS_LIST,
    {
      params: {
        ...(params.status ? { status: params.status } : {}),
      },
    },
  );
  return {
    ...response.data,
    campaigns: unwrapGamePassCollection<CampaignApiRecord>(
      response.data,
      "campaigns",
    ),
  };
};

/**
 * React Query hook bound to `GET /gamepass/v2/admin/campaigns` for a single
 * status (or unfiltered when `status` is omitted). Mirrors `useGetSeasonsList`
 * — 30s stale time, transient-error retry, previous-data placeholder.
 */
export const useGetCampaignsList = (
  params: CampaignsListQuery = {},
): UseQueryResult<CampaignsListResponse, Error> =>
  useQuery<CampaignsListResponse, Error>({
    queryKey: ["game-pass", "campaigns", "list", params.status ?? ""],
    queryFn: () => fetchCampaignsList(params),
    placeholderData: (previousData) => previousData,
    staleTime: 1000 * 30,
    refetchOnWindowFocus: false,
    retry: retryOnTransientErrors,
  });

/** Aggregated result of the multi-status campaigns hook. Kept narrow (only the
 *  parts the listing page consumes) so consumers don't grow accidental
 *  dependencies on internal React Query state. Mirrors
 *  `UseGetSeasonsListByStatusesResult`. */
export type UseGetCampaignsListByStatusesResult = {
  /** Flattened list of every campaign across every sub-query, concatenated in
   *  the order the statuses were requested. The lifecycle is mutually
   *  exclusive so no dedupe is needed. */
  data: ReadonlyArray<CampaignApiRecord>;
  /** True while ANY sub-query is in its first-load state (cached sub-queries
   *  that resolve instantly don't keep this true, so revisiting a loaded tab
   *  feels instant). */
  isLoading: boolean;
  /** True while ANY sub-query is fetching (incl. background refetches). */
  isFetching: boolean;
  /** True only when EVERY sub-query failed — a partial failure still surfaces
   *  the successful slice through `data`, matching the page's "fall through to
   *  empty cards on error" UX. */
  isError: boolean;
};

/**
 * Multi-status campaigns listing hook. Fires one parallel
 * `GET /gamepass/v2/admin/campaigns?status=<X>` request per entry in
 * `statuses`, aggregates them, and exposes a single result shape so the page
 * doesn't juggle N `UseQueryResult` objects. Mirrors
 * `useGetSeasonsListByStatuses`.
 *
 * `statuses === undefined` (the "All" tab) or `[]` collapses to a single
 * unfiltered fetch. Each sub-query has its own key so cache hits work
 * per-status and the 30s stale budget is independent per status.
 */
export const useGetCampaignsListByStatuses = (
  statuses?: ReadonlyArray<CampaignApiStatus>,
): UseGetCampaignsListByStatusesResult => {
  const queriesToFire: ReadonlyArray<CampaignApiStatus | undefined> =
    statuses && statuses.length > 0 ? statuses : [undefined];

  const results = useQueries({
    queries: queriesToFire.map((status) => ({
      queryKey: ["game-pass", "campaigns", "list", status ?? ""],
      queryFn: () => fetchCampaignsList(status ? { status } : {}),
      placeholderData: (previousData: CampaignsListResponse | undefined) =>
        previousData,
      staleTime: 1000 * 30,
      refetchOnWindowFocus: false,
      retry: retryOnTransientErrors,
    })),
  }) as ReadonlyArray<UseQueryResult<CampaignsListResponse, Error>>;

  const data = results.flatMap((result) => result.data?.campaigns ?? []);
  const isLoading = results.some((result) => result.isLoading);
  const isFetching = results.some((result) => result.isFetching);
  const isError =
    results.length > 0 && results.every((result) => result.isError);

  return { data, isLoading, isFetching, isError };
};
